/**
 * Cross-component sidebar-collapse control.
 *
 * The AppShell owns the `collapsed` state via `useState` (persisted to
 * localStorage). Pages that want to auto-collapse on mount (e.g.
 * `LeadDetailPage` - operators want the two-pane layout to claim every
 * pixel) call `requestSidebarCollapse(true)` here, and the AppShell
 * listens via a small custom-event hook.
 *
 * Why a custom event instead of a Context: the AppShell is mounted very
 * early in the tree and a Context would require either lifting the
 * state up further or refactoring every consumer. A window event is one
 * file, one listener, no provider boilerplate, and trivially testable.
 */

const SIDEBAR_COLLAPSE_EVENT = 'utazzo:sidebar-collapse-request';

interface SidebarCollapseDetail {
  collapsed: boolean;
}

/**
 * Ask the AppShell to collapse or expand the sidebar. Safe to call during
 * a render's `useEffect` cleanup-friendly window (i.e. from inside a
 * `useEffect`, never directly during render).
 */
export const requestSidebarCollapse = (collapsed: boolean): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SidebarCollapseDetail>(SIDEBAR_COLLAPSE_EVENT, {
      detail: { collapsed },
    }),
  );
};

/**
 * Subscribe to collapse requests. Used by the AppShell. The callback
 * receives the desired `collapsed` value; the AppShell decides whether to
 * apply it (e.g. it might choose to ignore on mobile where the sidebar is
 * a drawer rather than a collapsible rail).
 */
export const onSidebarCollapseRequest = (cb: (collapsed: boolean) => void): (() => void) => {
  const handler = (e: Event): void => {
    const detail = (e as CustomEvent<SidebarCollapseDetail>).detail;
    if (detail && typeof detail.collapsed === 'boolean') cb(detail.collapsed);
  };
  window.addEventListener(SIDEBAR_COLLAPSE_EVENT, handler);
  return () => window.removeEventListener(SIDEBAR_COLLAPSE_EVENT, handler);
};
